import { Bike, Check, Clock, Store, Truck } from "lucide-react";
import { formatCurrency } from "@veorest/utils";
import {
  partnerStageTranslationKey,
  type PartnerOrderRow,
} from "../../lib/partnerOrders";
import { formatAgo, isOlderThan } from "../../lib/partnerTime";
import { useI18n } from "../../i18n";

const WAIT_STAGES = new Set(["pending", "processing", "ready"]);
/** Cards older than this get the stalled outline (same tone as overdue). */
const STALL_MINUTES = 8;

interface OrderCardProps {
  order: PartnerOrderRow;
  selected: boolean;
  onSelect: () => void;
  onPrimary: () => void;
  primaryBusy?: boolean;
  /** Optional created/updated timestamp label. */
  timestamp?: string | null;
}

export function OrderCard({
  order,
  selected,
  onSelect,
  onPrimary,
  primaryBusy,
  timestamp,
}: OrderCardProps) {
  const { t, locale } = useI18n();
  const showAccept = order.stage === "pending" && order.fulfillment_type === "delivery";
  const needsDriver =
    (order.stage === "ready" || order.stage === "in_delivery") &&
    order.fulfillment_type === "delivery" &&
    !order.assigned_driver_id;
  const hasCta = showAccept || needsDriver;
  const isPickup = order.fulfillment_type === "pickup";
  const createdMs = order.created_at
    ? new Date(order.created_at).getTime()
    : Number.NaN;
  const waitVisible =
    WAIT_STAGES.has(order.stage) && Number.isFinite(createdMs);
  const waitStalled = waitVisible && isOlderThan(order.created_at, STALL_MINUTES);
  const waitLabel = waitVisible
    ? formatAgo(locale, Date.now() - createdMs, t("orders.waitNow"))
    : null;

  return (
    <article
      data-order-id={order.id}
      className={[
        "order-card order-card--compact",
        selected ? "order-card--selected" : "",
        hasCta ? "order-card--has-cta" : "",
        waitStalled ? "order-card--stalled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button type="button" className="order-card__body" onClick={onSelect}>
        <div className="order-card__row order-card__row--head">
          <span className="order-card__number">{order.displayNumber}</span>
          <h3 className="order-card__name">{order.tenant_name}</h3>
          <span className={`order-card__step order-card__step--${order.stage}`}>
            {t(partnerStageTranslationKey(order.stage))}
          </span>
          {order.status === "rejected" ? (
            <span className="order-card__flag order-card__flag--rejected">
              {t("orders.detail.returnToStore")}
            </span>
          ) : null}
          <span className="order-card__channel">
            {isPickup ? (
              <Store className="h-3 w-3" strokeWidth={2} aria-hidden />
            ) : (
              <Truck className="h-3 w-3" strokeWidth={2} aria-hidden />
            )}
            {t(isPickup ? "common.pickup" : "common.delivery")}
          </span>
          <span className="order-card__total">
            {formatCurrency(order.total, order.currency, locale)}
          </span>
        </div>
        <div className="order-card__row order-card__row--meta order-card__row--foot">
          <span className="order-card__meta-item order-card__meta-item--customer">
            {order.customer_name}
          </span>
          {waitLabel ? (
            <span
              className={`order-card__wait-chip ${
                waitStalled ? "order-card__wait-chip--stalled" : ""
              }`}
              title={timestamp ?? undefined}
            >
              <Clock className="h-3 w-3" strokeWidth={2} aria-hidden />
              {waitLabel}
            </span>
          ) : null}
          {needsDriver ? (
            <span className="order-card__flag">{t("orders.needsDriver")}</span>
          ) : null}
          {timestamp ? (
            <span className="order-card__meta-item">
              <Clock className="h-3 w-3" strokeWidth={2} aria-hidden />
              {timestamp}
            </span>
          ) : null}
          {order.driver_name ? (
            <span className="order-card__meta-item order-card__meta-item--driver">
              <Bike className="h-3 w-3" strokeWidth={2} aria-hidden />
              {order.driver_name}
            </span>
          ) : null}
        </div>
      </button>

      {hasCta ? (
        <div className="order-card__actions">
          <button
            type="button"
            className={`order-card__cta ${
              showAccept ? "order-card__cta--accept" : "order-card__cta--assign"
            }`}
            disabled={primaryBusy}
            onClick={(e) => {
              e.stopPropagation();
              onPrimary();
            }}
          >
            {showAccept ? (
              <Check className="h-4 w-4" strokeWidth={2.5} aria-hidden />
            ) : (
              <Bike className="h-4 w-4" strokeWidth={2.5} aria-hidden />
            )}
            {t(showAccept ? "orders.acceptShort" : "orders.assignShort")}
          </button>
        </div>
      ) : null}
    </article>
  );
}

export function OrderCardSkeleton() {
  return <div className="order-card order-card--compact h-[86px] skeleton" />;
}
